import { focusHaloFlush, panelMotion } from "@pax/ui";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { type ReactNode, useCallback, useRef, useState } from "react";
import {
  aboutOf,
  type CallFilter,
  type Dimension,
  dimensionGroups,
  dimensions,
  iconOf,
  nameOf,
  promptSizes,
} from "./dimensions";
import { capitalized, Mark, optionRow, Popover, panelHeading, Tick } from "./pieces";
import { type TimeWindow, useDimensionValues } from "./queries";

const chipBase = "inline-flex items-center rounded-full bg-foreground/[0.06]";

const chipRemove = `mr-1 grid size-5 place-items-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground ${panelMotion} ${focusHaloFlush}`;

/** Any filter as a chip: what it filters on, then what it keeps. Pressing it changes what it keeps. */
function Chip({
  label,
  icon,
  value,
  onRemove,
  children,
}: {
  label: string;
  icon?: string;
  value: string;
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <span className={chipBase}>
      <Popover
        faceClassName="flex h-7 items-center gap-1.5 pr-1 pl-2.5 text-[13px] whitespace-nowrap"
        face={
          <>
            <span className="text-muted-foreground">{label}</span>
            <Mark src={icon} size="size-3.5" />
            <span className="font-medium">{value}</span>
          </>
        }
      >
        {() => <div className="max-h-80 w-72 overflow-y-auto p-1">{children}</div>}
      </Popover>
      <button
        type="button"
        aria-label={`Remove the ${label} filter`}
        onClick={onRemove}
        className={chipRemove}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

const withoutDimension = (filter: CallFilter, dimension: Dimension): CallFilter => {
  const { [dimension]: _removed, ...keep } = filter.keep;
  return { ...filter, keep };
};

/** The values a dimension takes among the calls the other filters keep, to pick from. */
function ValueOptions({
  dimension,
  filter,
  window,
  chosen,
  onToggle,
}: {
  dimension: Dimension;
  filter: CallFilter;
  window: TimeWindow;
  chosen: string[];
  onToggle: (value: string) => void;
}) {
  const found = useDimensionValues(dimension, withoutDimension(filter, dimension), window);
  const values = [...new Set([...chosen, ...(found.data ?? [])])].sort((left, right) =>
    nameOf(dimension, left).localeCompare(nameOf(dimension, right)),
  );
  if (found.isPending) return <p className="px-3 py-2 text-sm text-muted-foreground">Reading…</p>;
  if (values.length === 0)
    return <p className="px-3 py-2 text-sm text-muted-foreground">No calls to filter.</p>;
  return (
    <>
      {values.map(value => (
        <button key={value} type="button" onClick={() => onToggle(value)} className={optionRow}>
          <Tick chosen={chosen.includes(value)} many />
          <Mark src={iconOf(dimension, value)} />
          <span className="min-w-0 flex-1">
            <span className="block truncate">{nameOf(dimension, value)}</span>
            {aboutOf(dimension, value) && (
              <span className="block truncate text-xs text-muted-foreground">
                {aboutOf(dimension, value)}
              </span>
            )}
          </span>
        </button>
      ))}
    </>
  );
}

export function FilterChip({
  dimension,
  values,
  filter,
  window,
  onChange,
}: {
  dimension: Dimension;
  values: string[];
  filter: CallFilter;
  window: TimeWindow;
  onChange: (values: string[]) => void;
}) {
  const first = values[0] ?? "";
  return (
    <Chip
      label={dimensions[dimension].label}
      icon={values.length === 1 ? iconOf(dimension, first) : undefined}
      value={
        values.length > 2
          ? `${nameOf(dimension, first)} or ${values.length - 1} more`
          : values.map(value => nameOf(dimension, value)).join(" or ")
      }
      onRemove={() => onChange([])}
    >
      <p className={panelHeading}>{dimensions[dimension].plural}</p>
      <ValueOptions
        dimension={dimension}
        filter={filter}
        window={window}
        chosen={values}
        onToggle={value =>
          onChange(
            values.includes(value) ? values.filter(chosen => chosen !== value) : [...values, value],
          )
        }
      />
    </Chip>
  );
}

/** Picking a size replaces the last; picking the chosen one clears it. */
function PromptOptions({
  promptOver,
  onPick,
}: {
  promptOver?: number;
  onPick: (tokens: number | undefined) => void;
}) {
  return (
    <>
      {promptSizes.map(([tokens, name]) => (
        <button
          key={tokens}
          type="button"
          onClick={() => onPick(promptOver === tokens ? undefined : tokens)}
          className={optionRow}
        >
          <Tick chosen={promptOver === tokens} many={false} />
          <span className="flex-1">{capitalized(name)}</span>
        </button>
      ))}
    </>
  );
}

export function PromptChip({
  promptOver,
  onChange,
}: {
  promptOver: number;
  onChange: (tokens: number | undefined) => void;
}) {
  return (
    <Chip
      label="prompt"
      value={promptSizes.find(([tokens]) => tokens === promptOver)?.[1] ?? `over ${promptOver}`}
      onRemove={() => onChange(undefined)}
    >
      <p className={panelHeading}>Prompt sizes</p>
      <PromptOptions promptOver={promptOver} onPick={onChange} />
    </Chip>
  );
}

type FilterStep = Dimension | "promptOver";

/**
 * Adding a filter is two steps in one panel: what to filter on, then which value to keep. Who
 * made a call and how it got there come first; the call itself comes last.
 */
export function AddFilter({
  filter,
  window,
  onAdd,
  onPromptOver,
}: {
  filter: CallFilter;
  window: TimeWindow;
  onAdd: (dimension: Dimension, value: string) => void;
  onPromptOver: (tokens: number | undefined) => void;
}) {
  const [step, setStep] = useState<FilterStep | null>(null);
  const kindsScrollTop = useRef(0);
  const restoreKindsScroll = useCallback((list: HTMLDivElement | null) => {
    if (list) list.scrollTop = kindsScrollTop.current;
  }, []);
  const reset = useCallback(() => {
    setStep(null);
    kindsScrollTop.current = 0;
  }, []);
  const groups: { label: string; steps: FilterStep[] }[] = dimensionGroups.map(group => ({
    label: group.label,
    steps:
      group.label === "About the call" ? [...group.dimensions, "promptOver"] : group.dimensions,
  }));
  const pluralOf = (id: FilterStep) =>
    id === "promptOver" ? "Prompt sizes" : dimensions[id].plural;

  return (
    <Popover
      view={step ?? "kinds"}
      onClosed={reset}
      faceClassName="flex h-7 items-center gap-1 rounded-full px-2.5 text-[13px] whitespace-nowrap text-muted-foreground hover:text-foreground"
      face={
        <>
          <Plus className="size-3.5" />
          Filter
        </>
      }
    >
      {close =>
        step ? (
          <div key={step} className="max-h-80 w-72 overflow-y-auto p-1">
            <button
              type="button"
              onClick={() => setStep(null)}
              className={`flex w-full items-center gap-1 rounded-xl px-2 pt-2 pb-1 text-xs text-muted-foreground hover:text-foreground ${focusHaloFlush}`}
            >
              <ChevronLeft className="size-3.5" />
              {pluralOf(step)}
            </button>
            {step === "promptOver" ? (
              <PromptOptions
                promptOver={filter.promptOver}
                onPick={tokens => {
                  onPromptOver(tokens);
                  close();
                }}
              />
            ) : (
              <ValueOptions
                dimension={step}
                filter={filter}
                window={window}
                chosen={filter.keep[step] ?? []}
                onToggle={value => {
                  onAdd(step, value);
                  close();
                }}
              />
            )}
          </div>
        ) : (
          <div
            key="kinds"
            ref={restoreKindsScroll}
            onScroll={event => {
              kindsScrollTop.current = event.currentTarget.scrollTop;
            }}
            className="max-h-80 w-60 overflow-y-auto p-1"
          >
            {groups.map(group => (
              <div key={group.label}>
                <p className={panelHeading}>{group.label}</p>
                {group.steps.map(id => (
                  <button key={id} type="button" onClick={() => setStep(id)} className={optionRow}>
                    <span className="flex-1">{pluralOf(id)}</span>
                    <ChevronRight className="size-3.5 text-muted-foreground" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        )
      }
    </Popover>
  );
}
